import axios from 'axios';
import router from '../router';
import store from '@/store/index';
import { API_URL, ACT_API_URL } from '@/config/index';
import { Toast } from 'vant';
import MD5 from 'crypto-js/md5';
import { getForumUid } from '@/utils/forum/forumUid';

const FORUM_CHANNEL = 'heimiao';
const FORUM_AUTH_REQUIRED = 'required';
const FORUM_AUTH_SILENT = 'silent';
const ACTIVE_SIGN_SALT = 'ACTIVe1276628964';

const isPlainObject = (data) => Object.prototype.toString.call(data) === '[object Object]';

const getRouteSnapshot = (route) => {
  if (!route || !route.name) return null;
  return {
    name: route.name,
    query: { ...(route.query || {}) },
    params: { ...(route.params || {}) },
  };
};

const openBindAccountDialog = () => {
  const appState = store.state.app || {};
  if (appState.showBindAccountDialog) return;
  const refreshRoute = getRouteSnapshot(router.currentRoute);
  if (refreshRoute && !appState.bindAccountRefreshRoute) {
    store.commit('app/SET_BIND_ACCOUNT_REFRESH_ROUTE', refreshRoute);
  }
  store.commit('app/CHANGE_BIND_ACCOUNT_DIALOG', true);
};

const injectForumParams = (config) => {
  if (!config.requireForumAuth) return;
  const authMode = config.forumAuthMode || FORUM_AUTH_REQUIRED;
  const data = config.data || {};
  if (!isPlainObject(data)) return;
  data.channel = FORUM_CHANNEL;
  if (data.uid === undefined) {
    const uid = getForumUid();
    if (!uid) {
      if (authMode === FORUM_AUTH_SILENT) {
        config.data = data;
        return;
      }
      openBindAccountDialog();
      return Promise.reject(new Error('FORUM_UID_REQUIRED'));
    }
    data.uid = uid;
  }
  config.data = data;
};

const injectActiveSignHeader = (config) => {
  if (!config.requireSign) return;
  const data = config.data || {};
  if (!isPlainObject(data) || data instanceof FormData || data.sign) return;
  config.headers['active-sign'] = MD5(`${JSON.stringify(data)}${ACTIVE_SIGN_SALT}`).toString().toLowerCase();
};

const getResponseMessage = (data) => data && (data.msg || data.message);

const markForumErrorShown = (error, message) => {
  if (!error || typeof error !== 'object') return;
  error.__forumErrorShown = true;
  error.__forumErrorMessage = message;
};

const handleForumResponse = (data, config) => {
  // 统一论坛平台沿用源应用口径：ret=1 / code=0 / success=true 都视为请求成功。
  if (data.ret === 1 || data.code === 0 || data.success === true) {
    return data;
  }
  const message = getResponseMessage(data);
  if (message && !config.unHint) {
    markForumErrorShown(data, message);
    Toast.fail({ message, duration: 2000 });
  }
  return Promise.reject(data);
};

const setRlaTokenInHeader = (config) => {
  const _KEY = 'rla_token_map'
  const tokenSet = JSON.parse(localStorage.getItem(_KEY) || '{}')
  Object.keys(tokenSet).forEach(k => {
    if (k.startsWith('rlaToken')) {
      config.headers[k] = tokenSet[k]
    }
  })
}

const setAnyTokenInHeader = (config, _KEY, keyPrefix) => {
  const tokenSet = JSON.parse(localStorage.getItem(_KEY) || '{}')
  Object.keys(tokenSet).forEach(k => {
    if (k.startsWith(keyPrefix)) {
      config.headers[k] = tokenSet[k]
    }
  })
}

class HttpRequest {
  getInsideConfig(options) {
    const { isAct, needSToken, needRlaToken, addToken, baseURL } = options
    const baseApi = baseURL || (!isAct ? API_URL : ACT_API_URL);
    const config = {
      baseURL: baseApi,
      headers: {
        token: localStorage.getItem('access_token'),
      },
    };
    if (needSToken) {
      const tokenNameList = localStorage.getItem('tokenNameList') || '';
      const tokenNameListArray = tokenNameList.split(',').filter(token => token);
      tokenNameListArray.forEach(token => {
        config.headers[token] = localStorage.getItem(token);
      })
    }
    if (needRlaToken) {
      // rlaToken 可能有多个不同的 key
      setRlaTokenInHeader(config)
    }
    if (addToken) {
      setAnyTokenInHeader(config, addToken._KEY, addToken.keyPrefix)
    } 
    return config;
  }
  interceptors(instance) {
    // 请求拦截
    instance.interceptors.request.use(
      config => {
        const injected = injectForumParams(config);
        if (injected) return injected;
        injectActiveSignHeader(config);
        return config;
      },
      error => {
        return Promise.reject(error);
      },
    );
    // 响应拦截
    instance.interceptors.response.use(
      res => {
        // Any status code that lie within the range of 2xx cause this function to trigger
        const { data, status, config } = res;
        if (status !== 200) return;
        if (config.isForumRequest) return handleForumResponse(data, config);
        if (data.code === 1 || data.ret === 1) {
          return data;
        } else if (data.code === 555) {
          // token过期特殊状态码，token过期，则清除本地缓存的token, 跳转路由的时候进行新token的获取
          localStorage.clear();
          store.commit('account/CHANGE_USERINFO', '');
          // 强制刷新页面走beforEach()钩子函数
          window.location.reload();
          return Promise.reject(data);
        } else if (data.code > 1000) {
          // 防刷提示
          Toast.fail('系统繁忙，请重试！');
          return Promise.reject(data);
        } else if (config.showToast) {
          Toast.fail(res.message ? res.message : res.data.message);
          return Promise.reject(data);
        } else {
          return Promise.reject(data);
        }
      },
      error => {
        // Any status codes that falls outside the range of 2xx cause this function to trigger
        let errorInfo = error.response;
        if (error.config && error.config.isForumRequest && !error.config.unHint) {
          const status = errorInfo && errorInfo.status;
          const message = status === 404 ? '系统繁忙，请稍后再试~' : getResponseMessage(errorInfo && errorInfo.data);
          if (message) {
            markForumErrorShown(errorInfo, message);
            markForumErrorShown(errorInfo && errorInfo.data, message);
            Toast.fail({ message, duration: 2000 });
          }
        }
        if (!errorInfo) {
          const {
            request: { status },
            config,
          } = JSON.parse(JSON.stringify(error));
          errorInfo = {
            status,
            request: {
              responseURL: config.url,
            },
          };
        }
        return Promise.reject(errorInfo);
      },
    );
  }
  request(options) {
    const instance = axios.create();
    options = Object.assign(this.getInsideConfig(options), options);
    this.interceptors(instance);
    return instance(options);
  }
}
const axiosRequest = new HttpRequest();
function request(data) {
  try {
    const r = axiosRequest.request(data);
    return r;
  } catch (error) {
    console.log(error, '请求错误');
  }
}

export default request;
